import React, { useState, useEffect } from 'react';
import ApiURl from "../Paths/Api";

const EventRegistrations = () => {
    const [registrations, setRegistrations] = useState([]);
    const [filteredRegistrations, setFilteredRegistrations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [filters, setFilters] = useState({
        search: '',
        meetingId: '',
        submittedDate: '',
        status: '',
        attendeeType: ''
    });
    const [showFilters, setShowFilters] = useState(false);
    const [meetings, setMeetings] = useState([]);
    const [selectedRegistration, setSelectedRegistration] = useState(null);
    const [selectedRegistrations, setSelectedRegistrations] = useState([]);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteSending, setInviteSending] = useState(false);
    const [bulkInviteSending, setBulkInviteSending] = useState(false);
    const [showBulkLimitModal, setShowBulkLimitModal] = useState(false);
    const [attendeeTypes, setAttendeeTypes] = useState([]);
    const [tableHeight] = useState('calc(100vh - 280px)');
    const MAX_BULK_SELECTION = 10;

    // Stats data
    const [attendeeStats, setAttendeeStats] = useState([]);
    const [topAttendeeTypes, setTopAttendeeTypes] = useState([]);

    useEffect(() => {
        fetchRegistrations();
        fetchMeetings();
    }, []);

    useEffect(() => {
        filterRegistrations();
    }, [filters, registrations]);

    useEffect(() => {
        // Extract unique attendee types from registrations
        const typesSet = new Set();
        registrations.forEach(reg => {
            const type = getAttendeeType(reg.form_data);
            if (type) {
                typesSet.add(type);
            }
        });
        setAttendeeTypes(Array.from(typesSet).sort());
    }, [registrations]);

    // Process stats whenever filteredRegistrations changes
    useEffect(() => {
        prepareStats();
    }, [filteredRegistrations]);

    const prepareStats = () => {
        // Count registrations by attendee type
        const typeCounts = {};
        filteredRegistrations.forEach(reg => {
            const type = getAttendeeType(reg.form_data) || 'General';
            typeCounts[type] = (typeCounts[type] || 0) + 1;
        });

        // Prepare stats for display
        const stats = Object.entries(typeCounts)
            .map(([type, count]) => ({
                type,
                count,
                percentage: filteredRegistrations.length > 0 
                    ? ((count / filteredRegistrations.length) * 100).toFixed(1) 
                    : '0'
            }))
            .sort((a, b) => b.count - a.count);

        setAttendeeStats(stats);
        setTopAttendeeTypes(stats.slice(0, 5)); // Top 5 for display
    };

    // ✅ Convert anything to clean string
    const safeText = (v) => (v === null || v === undefined ? '' : String(v)).trim();

    // ✅ Get all dynamic fields as array for UI
    const getFormFieldsArray = (formData) => {
        if (!formData || typeof formData !== "object") return [];
        return Object.entries(formData).map(([label, value]) => ({
            label: safeText(label),
            value: safeText(value),
        }));
    };

    // ✅ Find value by matching label keywords
    const getValueByLabel = (formData, keywords = []) => {
        if (!formData || typeof formData !== "object") return "";
        const lowerKeywords = keywords.map(k => k.toLowerCase());

        for (const [key, value] of Object.entries(formData)) {
            const k = safeText(key).toLowerCase();
            if (lowerKeywords.some(word => k.includes(word))) {
                return safeText(value);
            }
        }
        return "";
    };

    // ✅ Extract email dynamically
    const getEmail = (formData) =>
        getValueByLabel(formData, ["email", "email address", "mail"]);

    // ✅ Extract phone dynamically
    const getPhone = (formData) =>
        getValueByLabel(formData, ["phone", "mobile", "contact", "whatsapp", "telephone"]);

    // ✅ Extract full name dynamically
    const getName = (formData) => {
        const full = getValueByLabel(formData, ["full name", "name"]);
        const first = getValueByLabel(formData, ["first name", "fname"]);
        const last = getValueByLabel(formData, ["last name", "lname", "surname"]);

        // If full name exists use it
        if (full) return full;

        // Combine first + last
        const combined = `${first} ${last}`.trim();
        if (combined) return combined;

        // Fallback: find first non-empty field (avoid phone/email)
        const fields = getFormFieldsArray(formData);
        const fallback = fields.find(f =>
            f.value &&
            !f.label.toLowerCase().includes("email") &&
            !f.label.toLowerCase().includes("phone") &&
            !f.label.toLowerCase().includes("mobile") &&
            !f.label.toLowerCase().includes("whatsapp")
        );

        return fallback ? fallback.value : "";
    };

    // ✅ Extract attendee type/role dynamically
    const getAttendeeType = (formData) => {
        return getValueByLabel(formData, [
            "type", "attendee type", "role", "category",
            "participant type", "ticket type", "registration type",
            "member type", "user type"
        ]);
    };

    // ✅ Extract company/organization
    const getCompany = (formData) => {
        return getValueByLabel(formData, [
            "company", "organization", "firm", "institution",
            "university", "college", "school", "workplace"
        ]);
    };

    // ✅ Extract branch/location
    const getBranch = (formData) => {
        return getValueByLabel(formData, [
            "branch", "location", "office", "center", "campus", "city"
        ]);
    };

    // ✅ Extract designation/title
    const getDesignation = (formData) => {
        return getValueByLabel(formData, [
            "designation", "job title", "position", "title", "role"
        ]);
    };

    // ✅ Extract industry/sector
    const getIndustry = (formData) => {
        return getValueByLabel(formData, [
            "industry", "sector", "field", "domain"
        ]);
    };

    // ✅ Format date time to compact format
    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        }) + ' ' + date.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    // ✅ Format date only (for compact display)
    const formatDateOnly = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    // ✅ Format time only (for compact display)
    const formatTimeOnly = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    // ✅ Search inside all form values dynamically
    const matchSearchInFormData = (formData, term) => {
        if (!formData || typeof formData !== "object") return false;
        const lower = term.toLowerCase();
        return Object.entries(formData).some(([k, v]) => {
            const combined = `${safeText(k)} ${safeText(v)}`.toLowerCase();
            return combined.includes(lower);
        });
    };

    const fetchRegistrations = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${ApiURl}/get-event-registrations.php`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) throw new Error(`HTTP error ${response.status}`);
            const data = await response.json();

            if (data.success) {
                setRegistrations(data.registrations || []);
                setFilteredRegistrations(data.registrations || []);
                setSelectedRegistrations([]);
            } else {
                setError(data.message || 'Failed to fetch registrations');
            }
        } catch (err) {
            setError('Failed to load registrations: ' + err.message);
            console.error('Fetch registrations error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchMeetings = async () => {
        try {
            const response = await fetch(`${ApiURl}/get-meetings-list.php`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) throw new Error(`HTTP error ${response.status}`);
            const data = await response.json();

            if (data.success) {
                setMeetings(data.meetings || []);
            }
        } catch (err) {
            console.error('Fetch meetings error:', err);
        }
    };

    const filterRegistrations = () => {
        let filtered = [...registrations];

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter(reg =>
                (reg.meeting_title && reg.meeting_title.toLowerCase().includes(searchTerm)) ||
                (getName(reg.form_data) && getName(reg.form_data).toLowerCase().includes(searchTerm)) ||
                (getEmail(reg.form_data) && getEmail(reg.form_data).toLowerCase().includes(searchTerm)) ||
                (getPhone(reg.form_data) && getPhone(reg.form_data).toLowerCase().includes(searchTerm)) ||
                (getCompany(reg.form_data) && getCompany(reg.form_data).toLowerCase().includes(searchTerm)) ||
                (getBranch(reg.form_data) && getBranch(reg.form_data).toLowerCase().includes(searchTerm)) ||
                (getAttendeeType(reg.form_data) && getAttendeeType(reg.form_data).toLowerCase().includes(searchTerm)) ||
                (getDesignation(reg.form_data) && getDesignation(reg.form_data).toLowerCase().includes(searchTerm)) ||
                matchSearchInFormData(reg.form_data, searchTerm)
            );
        }

        if (filters.meetingId) {
            filtered = filtered.filter(reg => reg.meeting_id === filters.meetingId);
        }

        if (filters.submittedDate) {
            const selectedDate = new Date(filters.submittedDate);
            filtered = filtered.filter(reg => {
                const regDate = new Date(reg.submitted_at);
                return regDate.toDateString() === selectedDate.toDateString();
            });
        }

        if (filters.status) {
            filtered = filtered.filter(reg => reg.invite_status === filters.status);
        }

        if (filters.attendeeType) {
            filtered = filtered.filter(reg => {
                const type = getAttendeeType(reg.form_data);
                return type === filters.attendeeType;
            });
        }

        setFilteredRegistrations(filtered);
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const applyFilters = () => {
        filterRegistrations();
        setShowFilters(false);
    };

    const resetFilters = () => {
        setFilters({
            search: '',
            meetingId: '',
            submittedDate: '',
            status: '',
            attendeeType: ''
        });
        setFilteredRegistrations(registrations);
    };

    const handleViewDetails = (registration) => {
        setSelectedRegistration(registration);
        setShowDetailsModal(true);
    };

    const handleSelectRegistration = (registrationId) => {
        setSelectedRegistrations(prev => {
            if (prev.includes(registrationId)) {
                // Remove if already selected
                return prev.filter(id => id !== registrationId);
            } else {
                // Check if we can add more (max 10)
                if (prev.length >= MAX_BULK_SELECTION) {
                    setShowBulkLimitModal(true);
                    return prev; // Don't add, return previous state
                }
                return [...prev, registrationId];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectedRegistrations.length === filteredRegistrations.length) {
            setSelectedRegistrations([]);
        } else {
            // Limit selection to max 10 or total available
            const maxToSelect = Math.min(filteredRegistrations.length, MAX_BULK_SELECTION);
            const allIds = filteredRegistrations.slice(0, maxToSelect).map(reg => reg.id);
            setSelectedRegistrations(allIds);

            // If total registrations > 10, show warning
            if (filteredRegistrations.length > MAX_BULK_SELECTION) {
                setShowBulkLimitModal(true);
            }
        }
    };

    const sendBulkInvites = async () => {
        if (selectedRegistrations.length === 0) return;

        // Strictly limit to 10 items
        const registrationsToProcess = selectedRegistrations.slice(0, MAX_BULK_SELECTION);

        setBulkInviteSending(true);
        try {
            // Get the first selected registration's meeting ID
            const firstSelected = registrations.find(reg => registrationsToProcess.includes(reg.id));
            if (!firstSelected) {
                setError('No registrations selected');
                return;
            }

            const meetingId = firstSelected.meeting_id;

            // Prepare registrations data - only send to those not already sent
            const registrationsToSend = registrations
                .filter(reg => registrationsToProcess.includes(reg.id) && reg.invite_status !== 'sent')
                .map(reg => ({
                    id: reg.id,
                    name: getName(reg.form_data),
                    email: getEmail(reg.form_data),
                    phone: getPhone(reg.form_data)
                }))
                .filter(reg => reg.name && reg.email && reg.phone);

            if (registrationsToSend.length === 0) {
                setError('No valid registrations to send invites to');
                return;
            }

            const response = await fetch(`${ApiURl}/send-registration-invite.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'admin_invite',
                    meetingId: meetingId,
                    registrations: registrationsToSend
                }),
            });

            const data = await response.json();

            if (data.success) {
                const successCount = data.successCount || 0;
                const failCount = data.failCount || 0;

                setSuccess(`Successfully sent ${successCount} invite(s)! ${failCount > 0 ? `Failed: ${failCount}` : ''}`);

                const sentIds = (data.results || [])
                    .filter(r => r.success)
                    .map(r => r.registrationData ? r.registrationData.id : null);

                const updatedRegistrations = registrations.map(reg =>
                    sentIds.includes(reg.id)
                        ? { ...reg, invite_status: 'sent', invite_sent_at: new Date().toISOString() }
                        : reg
                );

                setRegistrations(updatedRegistrations);
                setFilteredRegistrations(updatedRegistrations.filter(reg =>
                    filteredRegistrations.some(fr => fr.id === reg.id)
                ));

                // Clear selection after sending
                setSelectedRegistrations([]);
                setShowBulkLimitModal(false);
            } else {
                setError(data.message || 'Failed to send invites');
            }
        } catch (err) {
            setError('Failed to send bulk invites: ' + err.message);
            console.error('Bulk invite error:', err);
        } finally {
            setBulkInviteSending(false);
            setTimeout(() => setSuccess(''), 3000);
        }
    };

    const exportToCSV = () => {
        if (filteredRegistrations.length === 0) return;

        const headers = ['ID', 'Event', 'Name', 'Email', 'Phone', 'Attendee Type', 'Company', 'Branch', 'Designation', 'Industry', 'Tickets', 'Amount', 'Submitted At', 'Invite Status'];

        const csvData = filteredRegistrations.map(reg => [
            reg.id,
            reg.meeting_title,
            getName(reg.form_data) || 'N/A',
            getEmail(reg.form_data) || 'N/A',
            getPhone(reg.form_data) || 'N/A',
            getAttendeeType(reg.form_data) || 'General',
            getCompany(reg.form_data) || 'N/A',
            getBranch(reg.form_data) || 'N/A',
            getDesignation(reg.form_data) || 'N/A',
            getIndustry(reg.form_data) || 'N/A',
            reg.num_tickets,
            reg.total_amount,
            formatDateTime(reg.submitted_at),
            reg.invite_status || 'not_sent'
        ]);

        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `event_registrations_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'sent':
                return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">Sent</span>;
            case 'pending':
                return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">Pending</span>;
            case 'not_sent':
            default:
                return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 border border-gray-200">Not Sent</span>;
        }
    };

    const getAttendeeTypeBadge = (formData) => {
        const type = getAttendeeType(formData);
        if (!type) return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 border border-gray-200">General</span>;

        const typeLower = type.toLowerCase();

        if (typeLower.includes('vip') || typeLower.includes('premium')) {
            return <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800 border border-purple-200">VIP</span>;
        }
        if (typeLower.includes('speaker') || typeLower.includes('presenter')) {
            return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 border border-blue-200">Speaker</span>;
        }
        if (typeLower.includes('sponsor') || typeLower.includes('partner')) {
            return <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800 border border-amber-200">Sponsor</span>;
        }
        if (typeLower.includes('student') || typeLower.includes('learner')) {
            return <span className="px-2 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">Student</span>;
        }

        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 border border-gray-200">{type}</span>;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const isCheckboxDisabled = (registrationId, index) => {
        // Checkbox is disabled if:
        // 1. Already selected (can be deselected)
        if (selectedRegistrations.includes(registrationId)) {
            return false;
        }
        // 2. Not selected and we already have 10 selected
        if (selectedRegistrations.length >= MAX_BULK_SELECTION) {
            return true;
        }
        return false;
    };

    // Simple CSS-based bar chart component
    const CSSBarChart = ({ data }) => {
        if (data.length === 0) return null;
        
        const maxCount = Math.max(...data.map(item => item.count));
        
        return (
            <div className="space-y-2">
                {data.map((item, index) => (
                    <div key={index} className="space-y-1">
                        <div className="flex justify-between text-xs">
                            <span className="font-medium truncate max-w-[120px]">{item.type}</span>
                            <span className="text-gray-600">{item.count} ({item.percentage}%)</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                            <div 
                                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${(item.count / maxCount) * 100}%` }}
                            ></div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // CSS-based pie chart (donut chart)
    const CSSDonutChart = ({ data }) => {
        if (data.length === 0) return null;
        
        const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
        const total = data.reduce((sum, item) => sum + item.count, 0);
        
        let cumulativePercent = 0;
        
        return (
            <div className="relative w-40 h-40 mx-auto">
                <svg className="w-full h-full" viewBox="0 0 100 100">
                    {data.map((item, index) => {
                        const percent = (item.count / total) * 100;
                        const startAngle = cumulativePercent * 3.6;
                        const endAngle = (cumulativePercent + percent) * 3.6;
                        cumulativePercent += percent;
                        
                        // Convert to SVG arc
                        const start = polarToCartesian(50, 50, 40, startAngle);
                        const end = polarToCartesian(50, 50, 40, endAngle);
                        const largeArcFlag = percent > 50 ? 1 : 0;
                        
                        const pathData = [
                            `M ${start.x} ${start.y}`,
                            `A 40 40 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
                            `L 50 50`,
                            'Z'
                        ].join(' ');
                        
                        return (
                            <path
                                key={index}
                                d={pathData}
                                fill={colors[index % colors.length]}
                                stroke="white"
                                strokeWidth="2"
                            />
                        );
                    })}
                    <circle cx="50" cy="50" r="20" fill="white" />
                    <text 
                        x="50" 
                        y="50" 
                        textAnchor="middle" 
                        dy="0.35em" 
                        className="text-sm font-bold fill-gray-700"
                    >
                        {total}
                    </text>
                    <text 
                        x="50" 
                        y="60" 
                        textAnchor="middle" 
                        dy="0.35em" 
                        className="text-xs fill-gray-500"
                    >
                        Total
                    </text>
                </svg>
                
                {/* Legend */}
                <div className="mt-4 space-y-1">
                    {data.slice(0, 3).map((item, index) => (
                        <div key={index} className="flex items-center text-xs">
                            <div 
                                className="w-3 h-3 rounded-sm mr-2"
                                style={{ backgroundColor: colors[index % colors.length] }}
                            ></div>
                            <span className="truncate flex-1">{item.type}</span>
                            <span className="font-medium ml-1">{item.count}</span>
                        </div>
                    ))}
                    {data.length > 3 && (
                        <div className="text-xs text-gray-500 text-center mt-1">
                            +{data.length - 3} more
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Helper function for pie chart
    const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
        const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
        return {
            x: centerX + (radius * Math.cos(angleInRadians)),
            y: centerY + (radius * Math.sin(angleInRadians))
        };
    };

    // Statistics Cards
    const StatCard = ({ title, value, icon, color }) => (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center">
                <div className={`p-2 rounded-lg ${color} mr-3`}>
                    {icon}
                </div>
                <div>
                    <p className="text-sm text-gray-500">{title}</p>
                    <p className="text-2xl font-bold text-gray-900">{value}</p>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6">
            {/* Header */}
            <div className="mb-4 pb-3 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <div className="flex items-center">
                        <button
                            onClick={() => window.history.back()}
                            className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 mr-4"
                        >
                            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            <span className="hidden xs:inline">Back</span>
                        </button>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Event Registrations</h1>
                            <p className="text-gray-600 text-sm mt-1">
                                Total: {filteredRegistrations.length} registration{filteredRegistrations.length !== 1 ? 's' : ''}
                                {filteredRegistrations.length !== registrations.length && (
                                    <span className="ml-2 text-gray-400">
                                        (filtered from {registrations.length})
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>

                    <div className="flex space-x-3 mt-3 sm:mt-0">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`flex items-center px-4 py-2 border text-sm font-medium rounded-lg ${showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                            </svg>
                            <span>Filters</span>
                            {Object.values(filters).some(f => f) && (
                                <span className="ml-2 w-2 h-2 bg-blue-500 rounded-full"></span>
                            )}
                        </button>
                        <button
                            onClick={fetchRegistrations}
                            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 flex items-center"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Alerts */}
            {success && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm animate-fade-in">
                    <div className="flex items-center">
                        <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-green-700">{success}</span>
                    </div>
                </div>
            )}

            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm animate-fade-in">
                    <div className="flex items-center">
                        <svg className="w-4 h-4 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-red-700">{error}</span>
                    </div>
                    <button
                        onClick={() => setError('')}
                        className="mt-1 text-red-600 hover:text-red-800 text-xs"
                    >
                        Dismiss
                    </button>
                </div>
            )}

            {/* Statistics Section */}
            {filteredRegistrations.length > 0 && (
                <div className="mb-6">
                    {/* Quick Stats Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <StatCard
                            title="Total Registrations"
                            value={filteredRegistrations.length}
                            icon={
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                            }
                            color="bg-blue-500"
                        />
                        <StatCard
                            title="Attendee Types"
                            value={attendeeStats.length}
                            icon={
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                            }
                            color="bg-green-500"
                        />
                        <StatCard
                            title="Top Type"
                            value={topAttendeeTypes[0] ? topAttendeeTypes[0].type : 'None'}
                            icon={
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                </svg>
                            }
                            color="bg-purple-500"
                        />
                        <StatCard
                            title="Avg. Tickets"
                            value={filteredRegistrations.length > 0 
                                ? (filteredRegistrations.reduce((sum, reg) => sum + reg.num_tickets, 0) / filteredRegistrations.length).toFixed(1)
                                : '0'
                            }
                            icon={
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            }
                            color="bg-amber-500"
                        />
                    </div>

                    {/* Charts Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Bar Chart Card */}
                        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="font-semibold text-gray-800">Registrations by Attendee Type</h3>
                                <span className="text-sm text-gray-500 font-medium">
                                    Top {Math.min(topAttendeeTypes.length, 5)} types
                                </span>
                            </div>
                            <div className="h-64 overflow-y-auto pr-2">
                                <CSSBarChart data={topAttendeeTypes.slice(0, 8)} />
                            </div>
                            {topAttendeeTypes.length > 8 && (
                                <div className="mt-2 text-xs text-gray-500 text-center">
                                    +{topAttendeeTypes.length - 8} more types
                                </div>
                            )}
                        </div>

                        {/* Donut Chart Card */}
                        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="font-semibold text-gray-800">Distribution Overview</h3>
                                <span className="text-sm text-gray-500 font-medium">
                                    {attendeeStats.length} categories
                                </span>
                            </div>
                            <div className="h-64 flex flex-col items-center justify-center">
                                <CSSDonutChart data={topAttendeeTypes.slice(0, 6)} />
                                <div className="mt-4 text-center">
                                    <p className="text-sm text-gray-600">
                                        Showing top {Math.min(topAttendeeTypes.length, 6)} of {attendeeStats.length} types
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Detailed Stats Table */}
                    {topAttendeeTypes.length > 0 && (
                        <div className="mt-6 bg-white rounded-lg border border-gray-200 p-4">
                            <h3 className="font-semibold text-gray-800 mb-3">Detailed Breakdown</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="py-2 px-3 text-left font-medium text-gray-700">Attendee Type</th>
                                            <th className="py-2 px-3 text-left font-medium text-gray-700">Count</th>
                                            <th className="py-2 px-3 text-left font-medium text-gray-700">Percentage</th>
                                            <th className="py-2 px-3 text-left font-medium text-gray-700">Bar</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {topAttendeeTypes.map((item, index) => (
                                            <tr key={index} className="hover:bg-gray-50">
                                                <td className="py-2 px-3">
                                                    <div className="flex items-center">
                                                        <div 
                                                            className="w-3 h-3 rounded-sm mr-2"
                                                            style={{ 
                                                                backgroundColor: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'][index % 5] 
                                                            }}
                                                        ></div>
                                                        <span>{item.type}</span>
                                                    </div>
                                                </td>
                                                <td className="py-2 px-3 font-medium">{item.count}</td>
                                                <td className="py-2 px-3">
                                                    <span className="inline-block px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                                                        {item.percentage}%
                                                    </span>
                                                </td>
                                                <td className="py-2 px-3">
                                                    <div className="w-full bg-gray-100 rounded-full h-2">
                                                        <div 
                                                            className="h-2 rounded-full"
                                                            style={{ 
                                                                width: `${item.percentage}%`,
                                                                backgroundColor: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'][index % 5]
                                                            }}
                                                        ></div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Compact Filters Panel */}
            {showFilters && (
                <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 shadow-sm animate-slide-down">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        {/* Search */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
                            <input
                                type="text"
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                placeholder="Name, email, phone..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        {/* Event Filter */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Event</label>
                            <select
                                value={filters.meetingId}
                                onChange={(e) => handleFilterChange('meetingId', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">All Events</option>
                                {meetings.map(meeting => (
                                    <option key={meeting.id} value={meeting.id}>
                                        {meeting.title}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Submitted Date Filter */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Submitted Date</label>
                            <input
                                type="date"
                                value={filters.submittedDate}
                                onChange={(e) => handleFilterChange('submittedDate', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        {/* Attendee Type Filter */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Attendee Type</label>
                            <select
                                value={filters.attendeeType}
                                onChange={(e) => handleFilterChange('attendeeType', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">All Types</option>
                                {attendeeTypes.map((type, index) => (
                                    <option key={index} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Status Filter */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                            <select
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">All Status</option>
                                <option value="not_sent">Not Sent</option>
                                <option value="sent">Sent</option>
                                <option value="pending">Pending</option>
                            </select>
                        </div>

                        {/* Filter Buttons */}
                        <div className="md:col-span-2 lg:col-span-1 flex gap-2 items-end">
                            <button
                                onClick={applyFilters}
                                className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 flex items-center justify-center"
                            >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                </svg>
                                Apply
                            </button>
                            <button
                                onClick={resetFilters}
                                className="flex-1 px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 flex items-center justify-center"
                            >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                Reset
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Actions Bar */}
            {selectedRegistrations.length > 0 && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg animate-fade-in">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        <div className="flex items-center">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                <span className="text-blue-700 font-bold text-sm">{selectedRegistrations.length}</span>
                            </div>
                            <div className="text-sm text-blue-800">
                                <span className="font-medium">{selectedRegistrations.length}</span> registration{selectedRegistrations.length !== 1 ? 's' : ''} selected
                                <span className="ml-2 text-amber-700 font-medium">
                                    (Max {MAX_BULK_SELECTION} allowed for bulk invite)
                                </span>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={sendBulkInvites}
                                disabled={bulkInviteSending || selectedRegistrations.length === 0}
                                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            >
                                {bulkInviteSending ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-1 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        Send Invites ({Math.min(selectedRegistrations.length, MAX_BULK_SELECTION)})
                                    </>
                                )}
                            </button>
                            <button
                                onClick={() => setSelectedRegistrations([])}
                                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 flex items-center"
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Clear
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {isLoading ? (
                <div className="flex flex-col justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-gray-600 text-lg">Loading registrations...</p>
                </div>
            ) : (
                <>
                    <div className="bg-white border border-gray-200 rounded overflow-hidden">
                        <div className="overflow-x-auto">
                            <div style={{ height: tableHeight, minHeight: '400px' }}>
                                <table className="min-w-full divide-y divide-gray-200 text-xs">
                                    <thead className="bg-gray-100 sticky top-0 z-10">
                                        <tr className="h-8">
                                            <th className="w-10 px-2 py-1 text-left">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedRegistrations.length === Math.min(filteredRegistrations.length, MAX_BULK_SELECTION)}
                                                    onChange={handleSelectAll}
                                                    className="h-3.5 w-3.5 text-blue-600 rounded"
                                                />
                                            </th>
                                            <th className="w-14 px-2 py-1 text-left font-medium text-gray-600">ID</th>
                                            <th className="px-2 py-1 text-left font-medium text-gray-600 min-w-[140px]">Name & Role</th>
                                            <th className="px-2 py-1 text-left font-medium text-gray-600 min-w-[160px]">Contact</th>
                                            <th className="w-28 px-2 py-1 text-left font-medium text-gray-600">Type</th>
                                            <th className="w-28 px-2 py-1 text-left font-medium text-gray-600">Org/Branch</th>
                                            <th className="w-32 px-2 py-1 text-left font-medium text-gray-600">Date</th>
                                            <th className="w-16 px-2 py-1 text-left font-medium text-gray-600">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {filteredRegistrations.length === 0 ? (
                                            <tr>
                                                <td colSpan={8} className="py-8 text-center text-gray-500 text-sm">
                                                    No registrations found
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredRegistrations.map((reg) => {
                                                const isSelected = selectedRegistrations.includes(reg.id);
                                                const disabledCheckbox = selectedRegistrations.length >= MAX_BULK_SELECTION && !isSelected;

                                                return (
                                                    <tr
                                                        key={reg.id}
                                                        className={`h-10 hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                                                    >
                                                        <td className="px-2 py-1">
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => handleSelectRegistration(reg.id)}
                                                                disabled={disabledCheckbox}
                                                                className={`h-3.5 w-3.5 rounded ${disabledCheckbox ? 'opacity-40 cursor-not-allowed' : ''}`}
                                                            />
                                                        </td>
                                                        <td className="px-2 py-1 font-mono text-gray-600">#{reg.id}</td>

                                                        <td className="px-2 py-1">
                                                            <div className="font-medium text-gray-900 leading-tight">
                                                                {getName(reg.form_data) || '—'}
                                                            </div>
                                                            <div className="text-gray-500 text-[10px] leading-tight">
                                                                {getDesignation(reg.form_data) || getAttendeeType(reg.form_data) || '—'}
                                                            </div>
                                                        </td>

                                                        <td className="px-2 py-1 leading-tight">
                                                            <div className="text-blue-600 hover:underline text-[11px]">
                                                                {getEmail(reg.form_data) || '—'}
                                                            </div>
                                                            <div className="text-gray-700 text-[11px]">
                                                                {getPhone(reg.form_data) || '—'}
                                                            </div>
                                                        </td>

                                                        <td className="px-2 py-1">
                                                            {getAttendeeTypeBadge(reg.form_data)}
                                                        </td>

                                                        <td className="px-2 py-1 text-gray-700 text-[11px] leading-tight">
                                                            {getCompany(reg.form_data) && (
                                                                <div>{getCompany(reg.form_data)}</div>
                                                            )}
                                                            {getBranch(reg.form_data) && (
                                                                <div className="text-gray-500">{getBranch(reg.form_data)}</div>
                                                            )}
                                                        </td>

                                                        <td className="px-2 py-1 text-gray-600 text-[11px] leading-tight">
                                                            <div>{formatDateOnly(reg.submitted_at)}</div>
                                                            <div className="text-gray-500">{formatTimeOnly(reg.submitted_at)}</div>
                                                        </td>

                                                        <td className="px-2 py-1">
                                                            <button
                                                                onClick={() => handleViewDetails(reg)}
                                                                className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                                                            >
                                                                View
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Footer Actions */}
            <div className="mt-4 flex flex-col sm:flex-row justify-between items-center gap-3">
                <div className="text-sm text-gray-600">
                    Showing all {filteredRegistrations.length} registration{filteredRegistrations.length !== 1 ? 's' : ''}
                    {selectedRegistrations.length > 0 && (
                        <span className="ml-2 font-medium">
                            • {selectedRegistrations.length} selected (max {MAX_BULK_SELECTION})
                        </span>
                    )}
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={exportToCSV}
                        disabled={filteredRegistrations.length === 0}
                        className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Export CSV ({filteredRegistrations.length})
                    </button>
                </div>
            </div>

            {/* Bulk Limit Modal */}
            {showBulkLimitModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg max-w-sm w-full">
                        <div className="p-4">
                            <div className="flex items-start mb-4">
                                <div className="flex-shrink-0">
                                    <svg className="h-6 w-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.698-.833-2.464 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-lg font-semibold text-gray-900">Bulk Invite Limit</h3>
                                    <div className="mt-2 text-sm text-gray-600">
                                        <p>You can select maximum <span className="font-bold">{MAX_BULK_SELECTION} registrations</span> for bulk invites at a time.</p>
                                        <p className="mt-1">This limit ensures optimal performance and deliverability.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4">
                                <div className="flex items-start">
                                    <svg className="h-4 w-4 text-amber-500 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div className="text-xs text-amber-800">
                                        Currently selected: <span className="font-bold">{selectedRegistrations.length}</span> registration(s)
                                        {selectedRegistrations.length > MAX_BULK_SELECTION && (
                                            <div className="mt-0.5 text-red-600 font-semibold">
                                                (Exceeds maximum limit!)
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end space-x-2">
                                <button
                                    onClick={() => {
                                        // Auto-trim to max 10 if exceeded
                                        if (selectedRegistrations.length > MAX_BULK_SELECTION) {
                                            const limitedSelection = selectedRegistrations.slice(0, MAX_BULK_SELECTION);
                                            setSelectedRegistrations(limitedSelection);
                                        }
                                        setShowBulkLimitModal(false);
                                    }}
                                    className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    OK
                                </button>
                                <button
                                    onClick={() => {
                                        // Strictly limit to 10 items
                                        const limitedSelection = selectedRegistrations.slice(0, MAX_BULK_SELECTION);
                                        setSelectedRegistrations(limitedSelection);
                                        setShowBulkLimitModal(false);
                                    }}
                                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                                >
                                    Keep First {MAX_BULK_SELECTION}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Details Modal */}
            {showDetailsModal && selectedRegistration && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 flex justify-between items-start">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">Registration Details</h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    #{selectedRegistration.id} • {selectedRegistration.meeting_title}
                                </p>
                            </div>
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <div className="text-xs font-medium text-gray-500 mb-1">Name</div>
                                    <div className="text-gray-900 font-semibold text-lg">{getName(selectedRegistration.form_data) || "N/A"}</div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <div className="text-xs font-medium text-gray-500 mb-1">Email</div>
                                    <div className="text-gray-900 text-lg">{getEmail(selectedRegistration.form_data) || "N/A"}</div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <div className="text-xs font-medium text-gray-500 mb-1">Phone</div>
                                    <div className="text-gray-900 text-lg">{getPhone(selectedRegistration.form_data) || "N/A"}</div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <div className="text-xs font-medium text-gray-500 mb-1">Attendee Type</div>
                                    <div className="text-gray-900 font-semibold">{getAttendeeType(selectedRegistration.form_data) || "General"}</div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <div className="text-xs font-medium text-gray-500 mb-1">Company</div>
                                    <div className="text-gray-900">{getCompany(selectedRegistration.form_data) || "N/A"}</div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <div className="text-xs font-medium text-gray-500 mb-1">Branch</div>
                                    <div className="text-gray-900">{getBranch(selectedRegistration.form_data) || "N/A"}</div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <div className="text-xs font-medium text-gray-500 mb-1">Designation</div>
                                    <div className="text-gray-900">{getDesignation(selectedRegistration.form_data) || "N/A"}</div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <div className="text-xs font-medium text-gray-500 mb-1">Industry</div>
                                    <div className="text-gray-900">{getIndustry(selectedRegistration.form_data) || "N/A"}</div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <div className="text-xs font-medium text-gray-500 mb-1">Submitted At</div>
                                    <div className="text-gray-900">
                                        {formatDateTime(selectedRegistration.submitted_at) || "N/A"}
                                    </div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <div className="text-xs font-medium text-gray-500 mb-1">Tickets</div>
                                    <div className="text-gray-900 font-semibold">{selectedRegistration.num_tickets}</div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <div className="text-xs font-medium text-gray-500 mb-1">Total Amount</div>
                                    <div className="text-gray-900 font-semibold text-lg">{formatCurrency(selectedRegistration.total_amount)}</div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <div className="text-xs font-medium text-gray-500 mb-1">Invite Status</div>
                                    <div className="text-gray-900 font-semibold">
                                        {selectedRegistration.invite_status === 'sent' ? 'Sent' :
                                            selectedRegistration.invite_status === 'pending' ? 'Pending' : 'Not Sent'}
                                    </div>
                                </div>
                            </div>

                            {/* Dynamic Form Fields */}
                            <div className="border rounded-lg overflow-hidden">
                                <div className="bg-gray-50 px-4 py-3 font-semibold text-sm text-gray-700 border-b">
                                    All Form Data
                                </div>

                                <div className="divide-y max-h-96 overflow-y-auto">
                                    {getFormFieldsArray(selectedRegistration.form_data).length === 0 ? (
                                        <div className="p-4 text-sm text-gray-500">No form data available</div>
                                    ) : (
                                        getFormFieldsArray(selectedRegistration.form_data).map((item, idx) => (
                                            <div key={idx} className="px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                                                <div className="text-sm font-medium text-gray-500 min-w-[200px]">{item.label}</div>
                                                <div className="text-sm text-gray-900 break-all text-left md:text-right">{item.value || "-"}</div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                className="px-6 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add these styles for animations */}
            <style jsx>{`
                @keyframes fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slide-down {
                    from { transform: translateY(-10px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                .animate-fade-in {
                    animation: fade-in 0.3s ease-out;
                }
                .animate-slide-down {
                    animation: slide-down 0.3s ease-out;
                }
            `}</style>
        </div>
    );
};

export default EventRegistrations;